import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NotificationItem } from './page';
import { formatDateTime as fmtDateTime, translateNotificationTitle, translateNotificationBody } from '@/lib/dateUtils';

interface Props {
  notification: NotificationItem;
  onClose: () => void;
}

export default function NotificationDetailSheet({ notification: n, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />

      {/* Sheet */}
      <div
        className="fixed z-[70] bg-white overflow-hidden
          bottom-0 left-0 right-0 rounded-t-3xl max-h-[92vh]
          lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2
          lg:w-full lg:max-w-lg lg:rounded-3xl lg:max-h-[85vh]
          flex flex-col"
        style={{ boxShadow: '0 -4px 40px rgba(0,0,0,0.15)' }}
      >
        {/* Handle bar (mobile) */}
        <div className="flex justify-center pt-3 pb-1 lg:hidden flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-200"></div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${n.color}15` }}>
              <i className={`${n.icon} text-lg`} style={{ color: n.color }}></i>
            </div>
            <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full capitalize" style={{ backgroundColor: `${n.color}15`, color: n.color }}>
              {n.notification_type}
            </span>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 text-[#374151] cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          <h2 className="text-[#1A1A1A] text-lg font-bold leading-snug mb-2">{translateNotificationTitle(n.notification_type, n.title, t)}</h2>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-4">
            {n.project && (
              <div className="flex items-center gap-1.5">
                <i className="ri-building-2-line text-[#9CA3AF] text-xs"></i>
                <span className="text-[#6B7280] text-xs font-medium">{n.project}</span>
              </div>
            )}
            {n.project && <span className="text-[#D1D5DB] text-xs">·</span>}
            <div className="flex items-center gap-1.5">
              <i className="ri-time-line text-[#9CA3AF] text-xs"></i>
              <span className="text-[#9CA3AF] text-xs">{fmtDateTime(n.created_at, lang)}</span>
            </div>
          </div>

          <div className="h-px bg-gray-100 mb-4"></div>

          <p className="text-[#374151] text-sm leading-relaxed mb-5">{translateNotificationBody(n.notification_type, n.body, t, lang)}</p>

          {/* Images */}
          {n.images && n.images.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-image-line text-[#9CA3AF] text-sm"></i>
                <span className="text-[#374151] text-sm font-semibold">
                  {n.images.length} {n.images.length === 1 ? t('notifDetail.image') : t('notifDetail.images')}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {n.images.map((img, i) => (
                  <button key={i} onClick={() => setLightboxIndex(i)} className="relative rounded-xl overflow-hidden aspect-[4/3] cursor-pointer group">
                    <img src={img} alt={`Attachment ${i + 1}`} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-white/0 group-hover:bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                        <i className="ri-zoom-in-line text-white text-base"></i>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Files */}
          {n.files && n.files.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-attachment-2 text-[#9CA3AF] text-sm"></i>
                <span className="text-[#374151] text-sm font-semibold">
                  {n.files.length} {n.files.length === 1 ? t('notifDetail.file') : t('notifDetail.files')}
                </span>
              </div>
              <div className="space-y-2">
                {n.files.map((file, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#F9FAFB] border border-gray-100">
                    <div className="w-9 h-9 rounded-lg bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
                      <i className={`${file.type === 'pdf' ? 'ri-file-pdf-line' : 'ri-file-line'} text-[#E8621A] text-base`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#374151] text-sm font-medium truncate">{file.name}</p>
                      <p className="text-[#9CA3AF] text-xs">{file.size}</p>
                    </div>
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-[#9CA3AF]">
                      <i className="ri-download-line text-base"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="w-full py-3 rounded-xl bg-[#F5F5F3] text-[#374151] text-sm font-semibold cursor-pointer transition-colors hover:bg-gray-200 whitespace-nowrap">
            {t('btn.close')}
          </button>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxIndex !== null && n.images && (
        <div className="fixed inset-0 bg-black/95 z-[80] flex flex-col" onClick={() => setLightboxIndex(null)}>
          <div className="flex items-center justify-between px-4 py-4 flex-shrink-0">
            <p className="text-white/60 text-sm">{lightboxIndex + 1} / {n.images.length}</p>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer" onClick={() => setLightboxIndex(null)}>
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <img src={n.images[lightboxIndex]} alt={`Image ${lightboxIndex + 1}`} className="max-w-full max-h-full rounded-2xl object-contain" />
          </div>
          <div className="flex items-center justify-center gap-4 px-4 py-4 flex-shrink-0">
            <button
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer disabled:opacity-30"
              disabled={lightboxIndex === 0}
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i !== null ? Math.max(0, i - 1) : 0)); }}
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer disabled:opacity-30"
              disabled={lightboxIndex === n.images.length - 1}
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i !== null ? Math.min(n.images!.length - 1, i + 1) : 0)); }}
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
